import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Schema } from "rsuite";
import { Icon } from "@rsuite/icons";
import { Save, ImportStorage } from "@rsuite/icons";
import CallsFilters from "./CallsFilters";
import CallsPacketsTable from "./CallsPacketsTable";
import DarkTable from "components/Table/DarkTable";
import Panel from "components/base/Panel";
import {
    PageContainer
} from "components/Page/PageComponents";
import { useData } from "hooks/useData";
import { callsMock } from "mocks/callsMock";
import { handleDownload } from "utils";
import { lastMinutesToEndOfDayUTC, toBackendUTCString } from "utils/time";
import { CallFilters, callFormModel } from "types/filterConfig";
import { useGetCallsQuery, useOrderDumpMutation, useDownloadDumpMutation } from "store/apiSlice";

import TableActionButton from "components/Button/TableActionButton";

interface CallsPageProps {
    initFilters: CallFilters
};

const CallsPage: React.FC<CallsPageProps> = ({ initFilters }) => {

    const [refreshKey, setRefreshKey] = useState(Date.now());
    const [disabledCalls, setDisabledCalls] = useState<string[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    const [formError, setFormError] = useState<Record<string, string>>({});
    const filterKeys: (keyof CallFilters)[] = [
        "number_from",
        "number_to",
        "ip_src",
        "ip_dst",
        // "call_id",
        // "method_code",
        "time_from",
        "time_to"
    ];

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let mutated = false;

        // підставляємо інші ключі з initFilters, якщо відсутні
        for (const k of filterKeys) {
            if (!params.has(k) && initFilters[k] !== undefined) {
                params.set(k, String(initFilters[k] ?? ""));
                mutated = true;
            }
        }

        // якщо немає time_*, виставляємо останні 5 хв у UTC (обрізані до хвилини)
        const hasTimeFrom = params.has("time_from");
        const hasTimeTo = params.has("time_to");
        if (!hasTimeFrom || !hasTimeTo) {
            const { from, to } = lastMinutesToEndOfDayUTC(5);
            params.set("time_from", toBackendUTCString(from));
            params.set("time_to", toBackendUTCString(to));
            mutated = true;
        }

        if (mutated) {
            setSearchParams(params, { replace: true });
        }
    }, []);

    const savedFilters = useMemo<CallFilters>(() => {
        const filters: CallFilters = { ...initFilters };

        filterKeys.forEach((key) => {
            const value = searchParams.get(key);
            if (value !== null) {
                filters[key] = value;
            }
        });

        const validation = callFormModel.check(filters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        return filters;
    }, [initFilters, searchParams]);

    useEffect(() => {
        setFilters(savedFilters);
    }, [savedFilters]);

    const [filters, setFilters] = useState<CallFilters>(savedFilters);
    const {
        data: calls,
        isLoading,
        isFetching,
        error
    } = useData(
        useGetCallsQuery,
        callsMock,
        {
            time_from: filters.time_from,
            time_to: filters.time_to,
            number_from: filters.number_from,
            number_to: filters.number_to,
            ip_src: filters.ip_src,
            ip_dst: filters.ip_dst,
            // call_id: filters.call_id,
            // method_code: filters.method_code,
            refreshKey
        },
        filters === initFilters
    );

    const [orderDump] = useOrderDumpMutation();
    const [downloadDump] = useDownloadDumpMutation();

    const columns = useMemo(() => [
        { label: "Start Time", dataKey: "time", flexGrow: 1, align: "left", resizable: false },
        {
            label: "Incoming IP",
            dataKey: "ip_src",
            value: ({ ip_src, port_src }: any) => {
                return `${ip_src}:${port_src}`;
            },
            minWidth: 180,
            align: "left",
            resizable: false
        },
        {
            label: "Outgoing IP",
            dataKey: "ip_dst",
            value: ({ ip_dst, port_dst }: any) => {
                return `${ip_dst}:${port_dst}`;
            },
            minWidth: 180,
            align: "left",
            resizable: false
        },
        { label: "Calling Number", dataKey: "number_from", flexGrow: 1, align: "left", resizable: false },
        { label: "Called Number", dataKey: "number_to", flexGrow: 1, align: "left", resizable: false },
        { label: "Packet Count", dataKey: "packets_count", width: 100, align: "right", resizable: false },
        { label: "Session time", dataKey: "session_time", width: 120, align: "right", resizable: false },
        {
            label: "Action",
            dataKey: "action",
            width: 145,
            align: "center",
            value: ({ call_id, time, dump_filename, dump_completed = 0, dump_error = null }: any) => {
                const disabledOrderDump = disabledCalls.includes(call_id);
                const downloadButtonText = error ? "Unavailable" : "Download";
                const orderButtonText = error ? "Unavailable" : "Order dump";
                return <>
                    {dump_completed
                        ? <TableActionButton
                            disabled={!!dump_error}
                            small={!!dump_error}
                            onClick={async () => {
                                const blob = await downloadDump({ dump_filename }).unwrap();
                                handleDownload(dump_filename, blob);
                            }}
                        >
                            <Icon as={Save} />
                            <span>{downloadButtonText}</span>
                        </TableActionButton>
                        : <TableActionButton
                            disabled={disabledOrderDump || !!dump_error}
                            small={!!dump_error}
                            onClick={() => {
                                orderDump({ call_id });
                                setDisabledCalls((prev) => [...prev, call_id]);
                            }}
                        >
                            <Icon as={ImportStorage} />
                            <span>{orderButtonText}</span>
                        </TableActionButton>
                    }
                </>;
            }
        }
    ], [disabledCalls, orderDump, downloadDump]);

    const [filtersExpanded, setFiltersExpanded] = useState(true);

    const [currentCallId, setCurrentCallId] = useState(null);
    const [currentCallData, setCurrentCallData] = useState(null);

    const loading = isLoading || isFetching;

    useEffect(() => {
        if (calls && calls.length) {
            const firstCall = calls[0];
            setCurrentCallId(firstCall.call_id);
            setCurrentCallData(firstCall);
        } else {
            setCurrentCallId(null);
            setCurrentCallData(null);
        }
    }, [calls]);

    return (
        <PageContainer>
            <CallsFilters
                expanded={filtersExpanded}
                model={callFormModel}
                loading={loading}
                filters={filters}
                formError={formError}
                setFilters={setFilters}
                setFormError={setFormError}
                setRefreshKey={setRefreshKey}
                setSearchParams={setSearchParams}
            />
            <Panel noStyles noEndPadding>
                <DarkTable
                    data={calls}
                    columns={columns}
                    loading={loading}
                    error={error}
                    showSkeleton
                    // showError
                    rowHeight={40}
                    headerHeight={40}
                    height={320}
                    bordered
                    cellBordered

                    onRowClick={({ call_id, ...rowData }) => {
                        setCurrentCallId(call_id);
                        setCurrentCallData(rowData);
                    }}
                    rowClassName={(props: any, rowIndex: number) => {
                        if (props && props.call_id === currentCallId) {
                            return "current_row";
                        }
                        return "";
                    }}
                />

                <CallsPacketsTable
                    callId={currentCallId}
                    callData={currentCallData}
                    callsLoading={loading}
                />
            </Panel>
        </PageContainer>
    );
};

export default CallsPage; 